import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Autocomplete,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import {
  TorneioPorIdData,
  CategoriaTorneio,
  InscricoesPorTorneioData,
} from '../../../services/torneioService';
import InscricaoStatusChip from '../../../components/admin/InscricaoStatusChip';
import { useAuth } from '../../../context/AuthContext';
import { canManageInscricoes } from '../../../utils/torneioPermissions';
import { buscarFilasEsperaPorTorneio, FilaEspera } from '../../../services/filaEsperaService';
import { INSCRICAO_STATUS_FILTER_OPTIONS } from '../../../utils/inscricaoStatusDisplay';

interface TournamentRegistrationsTabProps {
  torneioId: string;
  torneio: TorneioPorIdData['torneioPorId'];
  inscricoes: InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'];
  categorias: CategoriaTorneio[];
  onPromoverFilaEsperaPorCategoria?: (categoriaId: string) => Promise<void>;
  onPromoverFilaEsperaPorId?: (filaEsperaId: string) => Promise<void>;
  promotingFila?: boolean;
}

type InscricaoItemGraphQL = InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'][0];

interface Filters {
  categoriaId: string | null;
  dataInicio: string | null;
  dataFim: string | null;
  nomeUsuario: string;
  status: string;
}

type OrderBy = 'dataInscricao' | 'nomeAtleta' | null;
type Order = 'asc' | 'desc';

const formatDate = (value?: string | null) =>
  value ? (dayjs(value).isValid() ? dayjs(value).format('DD/MM/YYYY HH:mm') : '') : '';

const STATUS_OPTIONS = [
  { label: 'Todos', value: 'TODOS' },
  ...INSCRICAO_STATUS_FILTER_OPTIONS,
];

/**
 * Componente da aba "Inscrições" do gerenciamento de torneio.
 * 
 * Exibe todas as inscrições do torneio em uma tabela com filtros por categoria,
 * data, nome do usuário e status.
 */
const TournamentRegistrationsTab: React.FC<TournamentRegistrationsTabProps> = ({
  torneioId,
  torneio,
  inscricoes,
  categorias,
  onPromoverFilaEsperaPorCategoria,
  onPromoverFilaEsperaPorId,
  promotingFila,
}) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);

  const hasPermission = useMemo(() => {
    if (!torneio || !user) return false;
    return canManageInscricoes(torneio, user.id, user.role);
  }, [torneio, user]);
  const [filters, setFilters] = useState<Filters>({
    categoriaId: null,
    dataInicio: null,
    dataFim: null,
    nomeUsuario: '',
    status: 'TODOS',
  });

  const [orderBy, setOrderBy] = useState<OrderBy>('dataInscricao');
  const [order, setOrder] = useState<Order>('desc');
  const [filasEspera, setFilasEspera] = useState<FilaEspera[]>([]);

  const fetchFilasEspera = useCallback(async () => {
    try {
      const filas = await buscarFilasEsperaPorTorneio(torneioId);
      setFilasEspera(filas);
    } catch (error) {
      console.error('Erro ao buscar filas de espera:', error);
    }
  }, [torneioId]);

  useEffect(() => {
    fetchFilasEspera();
  }, [fetchFilasEspera]);

  const handlePromoverPorCategoria = async (categoriaId: string) => {
    if (onPromoverFilaEsperaPorCategoria) {
      await onPromoverFilaEsperaPorCategoria(categoriaId);
      await fetchFilasEspera();
    }
  };

  const handlePromoverPorId = async (filaEsperaId: string) => {
    if (onPromoverFilaEsperaPorId) {
      await onPromoverFilaEsperaPorId(filaEsperaId);
      await fetchFilasEspera();
    }
  };

  const getFilaEsperaId = (inscricaoId: string): string | null => {
    const fila = filasEspera.find(f => f.inscricao.id === inscricaoId);
    return fila?.id ?? null;
  };

  const getWaitlistPosition = (inscricaoId: string): number | null => {
    const fila = filasEspera.find(f => f.inscricao.id === inscricaoId);
    return fila?.posicao ?? null;
  };

  // Opções de categoria para o Autocomplete
  const categoriaOptions = useMemo(() => {
    return categorias.map((cat) => ({
      id: cat.id,
      label: cat.nome,
    }));
  }, [categorias]);

  // Validação de datas
  const hasDateError = useMemo(() => {
    if (!filters.dataInicio || !filters.dataFim) return false;
    const inicio = dayjs(filters.dataInicio);
    const fim = dayjs(filters.dataFim);
    if (!inicio.isValid() || !fim.isValid()) return false;
    return inicio.isAfter(fim);
  }, [filters.dataInicio, filters.dataFim]);

  // Filtragem de inscrições
  const filteredInscricoes = useMemo(() => {
    return inscricoes.filter((inscricao) => {
      // Filtro por categoria
      if (filters.categoriaId) {
        if (inscricao.categoriaTorneio?.id !== filters.categoriaId) {
          return false;
        }
      }

      // Filtro por data
      if (filters.dataInicio || filters.dataFim) {
        const dataInscricao = inscricao.dataInscricao;
        if (!dataInscricao) return false;

        const data = dayjs(dataInscricao);
        if (!data.isValid()) return false;

        if (filters.dataInicio) {
          const inicio = dayjs(filters.dataInicio).startOf('day');
          if (data.isBefore(inicio)) return false;
        }

        if (filters.dataFim) {
          const fim = dayjs(filters.dataFim).endOf('day');
          if (data.isAfter(fim)) return false;
        }
      }

      // Filtro por nome do usuário
      if (filters.nomeUsuario.trim()) {
        const atletas = inscricao.equipe?.atletas || [];
        const hasMatch = atletas.some((atleta) => {
          const nome = atleta.usuario?.nome || '';
          return nome.toLowerCase().includes(filters.nomeUsuario.toLowerCase());
        });
        if (!hasMatch) return false;
      }

      // Filtro por status
      if (filters.status && filters.status !== 'TODOS') {
        if (inscricao.status !== filters.status) {
          return false;
        }
      }

      return true;
    });
  }, [inscricoes, filters]);

  // Ordenação
  const sortedInscricoes = useMemo(() => {
    if (!orderBy) return filteredInscricoes;

    return [...filteredInscricoes].sort((a, b) => {
      let comparison = 0;

      if (orderBy === 'dataInscricao') {
        // Tratar valores nulos: inscrições sem data aparecem por último
        if (!a.dataInscricao && !b.dataInscricao) return 0;
        if (!a.dataInscricao) return 1;
        if (!b.dataInscricao) return -1;
        
        const dataA = dayjs(a.dataInscricao).valueOf();
        const dataB = dayjs(b.dataInscricao).valueOf();
        comparison = dataA - dataB;
      } else if (orderBy === 'nomeAtleta') {
        const nomeA = a.equipe?.atletas?.[0]?.usuario?.nome || '';
        const nomeB = b.equipe?.atletas?.[0]?.usuario?.nome || '';
        comparison = nomeA.localeCompare(nomeB, 'pt-BR');
      }

      return order === 'asc' ? comparison : -comparison;
    });
  }, [filteredInscricoes, orderBy, order]);

  const handleSort = (field: OrderBy) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(field);
      // Começar com 'asc' para nome (alfabética), 'desc' para data (mais recente primeiro)
      setOrder(field === 'nomeAtleta' ? 'asc' : 'desc');
    }
  };

  const handleResetFilters = () => {
    setFilters({
      categoriaId: null,
      dataInicio: null,
      dataFim: null,
      nomeUsuario: '',
      status: 'TODOS',
    });
  };

  const handleFilterChange = useCallback((updates: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleNovaInscricao = () => {
    navigate(`/liga/tournament/${torneioId}/inscricao/nova`);
  };

  const handleEditarInscricao = (inscricaoId: string) => {
    navigate(`/liga/tournament/${torneioId}/inscricao/${inscricaoId}/editar`);
  };

  return (
    <div className="mt-4">
      {/* Barra de Filtros */}
      <Box className="card w-100 border-0 bg-white shadow-xs p-3 mb-3">
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
          {/* Filtro por Categoria */}
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <Autocomplete
              options={categoriaOptions}
              getOptionLabel={(option) => option.label}
              value={categoriaOptions.find((opt) => opt.id === filters.categoriaId) || null}
              onChange={(_, value) => handleFilterChange({ categoriaId: value?.id || null })}
              renderInput={(params) => (
                <TextField {...params} label="Categoria" size="small" />
              )}
            />
          </Box>

          {/* Filtro por Nome do Usuário */}
          <Box sx={{ flex: '1 1 240px', minWidth: 220 }}>
            <TextField
              label="Nome do Atleta"
              size="small"
              fullWidth
              value={filters.nomeUsuario}
              onChange={(e) => handleFilterChange({ nomeUsuario: e.target.value })}
              placeholder="Buscar por nome"
            />
          </Box>

          {/* Filtro por Status */}
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              value={filters.status || 'TODOS'}
              onChange={(e) =>
                handleFilterChange({ status: e.target.value })
              }
              slotProps={{ inputLabel: { shrink: true } }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Filtro por Data Início */}
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Início"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dataInicio || ''}
              onChange={(e) => handleFilterChange({ dataInicio: e.target.value || null })}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>

          {/* Filtro por Data Fim */}
          <Box sx={{ flex: '1 1 180px', minWidth: 160 }}>
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dataFim || ''}
              onChange={(e) => handleFilterChange({ dataFim: e.target.value || null })}
              error={hasDateError}
              helperText={hasDateError ? 'Data início maior que data fim' : undefined}
            />
          </Box>

          {/* Botões */}
          <Box sx={{ flex: '0 1 auto' }}>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={handleResetFilters}
              >
                Limpar
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Botões Nova Inscrição e Promover Fila */}
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        {hasPermission && torneio.autoFilaEspera === false && onPromoverFilaEsperaPorCategoria && filters.categoriaId && filasEspera.some(f => f.inscricao?.categoriaTorneio?.id === filters.categoriaId) && (
          <Tooltip title="Promover o próximo da fila de espera desta categoria para Pendente (segue a ordem da fila)">
            <span>
              <Button
                variant="outlined"
                color="success"
                startIcon={promotingFila ? <CircularProgress size={16} /> : <ArrowUpwardIcon />}
                onClick={() => handlePromoverPorCategoria(filters.categoriaId!)}
                disabled={promotingFila}
              >
                Promover Fila
              </Button>
            </span>
          </Tooltip>
        )}
        {hasPermission && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNovaInscricao}
          >
            Nova Inscrição
          </Button>
        )}
      </Box>

      {/* Tabela de Inscrições */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#6c757d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'nomeAtleta'}
                  direction={orderBy === 'nomeAtleta' ? order : 'asc'}
                  onClick={() => handleSort('nomeAtleta')}
                  style={{ color: 'inherit' }}
                >
                  Nome do Atleta
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Categoria</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderBy === 'dataInscricao'}
                  direction={orderBy === 'dataInscricao' ? order : 'asc'}
                  onClick={() => handleSort('dataInscricao')}
                  style={{ color: 'inherit' }}
                >
                  Data de Inscrição
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Observações</TableCell>
              {hasPermission && (
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Ações</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInscricoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={hasPermission ? 6 : 5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {inscricoes.length === 0
                      ? 'Nenhuma inscrição encontrada para este torneio.'
                      : 'Nenhuma inscrição corresponde aos filtros aplicados.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedInscricoes.map((inscricao) => {
                const atletas = inscricao.equipe?.atletas || [];
                const primeiroAtleta = atletas[0];
                const nomeEquipe = atletas.map(a => a.usuario?.nome).filter(Boolean).join(' / ') || 'Sem nome';
                return (
                <TableRow key={inscricao.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {primeiroAtleta?.usuario?.imagemUrl ? (
                        <Avatar
                          src={primeiroAtleta.usuario.imagemUrl}
                          alt={primeiroAtleta.usuario.nome}
                          sx={{ width: 32, height: 32 }}
                        />
                      ) : (
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {primeiroAtleta?.usuario?.nome?.charAt(0).toUpperCase() || '?'}
                        </Avatar>
                      )}
                      <Typography variant="body2">
                        {nomeEquipe}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {inscricao.categoriaTorneio?.nome || 'Sem categoria'}
                  </TableCell>
                  <TableCell>{formatDate(inscricao.dataInscricao)}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      
                      {(() => {
                        const temReembolsoAtivo = inscricao.pagamentoInscricao?.pedidosReembolso?.some(
                          (p) => p.status !== 'RECUSADO' && p.status !== 'FINALIZADO'
                        );
                        if (temReembolsoAtivo) {
                          return <InscricaoStatusChip status="PEDIDO_REEMBOLSO" />;
                        }
                        if (inscricao.status) {
                          return (
                            <InscricaoStatusChip
                              status={inscricao.status}
                              pagamentoStatus={inscricao.pagamentoInscricao?.status}
                              isento={inscricao.isento}
                            />
                          );
                        }
                        return (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        );
                      })()}
                      {inscricao.status === 'FILA_ESPERA' && (
                        <Typography variant="body2" fontWeight="bold" color="text.secondary">
                          #{getWaitlistPosition(inscricao.id)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {inscricao.observacao || '-'}
                    </Typography>
                  </TableCell>
                  {hasPermission && (
                    <TableCell>
                      <Tooltip title="Editar Inscrição">
                        <IconButton
                          size="small"
                          onClick={() => handleEditarInscricao(inscricao.id)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {inscricao.status === 'FILA_ESPERA' && torneio.autoFilaEspera === false && onPromoverFilaEsperaPorId && getFilaEsperaId(inscricao.id) && (
                        <Tooltip title="Promover esta inscrição da fila de espera para Pendente (ignora a ordem da fila)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handlePromoverPorId(getFilaEsperaId(inscricao.id)!)}
                              color="success"
                              disabled={promotingFila}
                            >
                              {promotingFila ? <CircularProgress size={16} /> : <ArrowUpwardIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Informação de total */}
      {sortedInscricoes.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Total de inscrições: {sortedInscricoes.length}
            {inscricoes.length !== sortedInscricoes.length &&
              ` de ${inscricoes.length} (filtradas)`}
          </Typography>
        </Box>
      )}
    </div>
  );
};

export default TournamentRegistrationsTab;
